import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useLeadSearch, useLeadById, type LeadSearchResult } from "@/hooks/leads/useLeadSearch";

interface LeadSearchInputProps {
  value: string | null;
  onChange: (leadId: string | null, lead: LeadSearchResult | null) => void;
  defaultLeadId?: string;
  disabled?: boolean;
  className?: string;
}

export function LeadSearchInput({
  value,
  onChange,
  defaultLeadId,
  disabled,
  className,
}: LeadSearchInputProps) {
  const [open, setOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadSearchResult | null>(null);
  const { leads, isLoading, query, setQuery } = useLeadSearch();
  const { data: defaultLead } = useLeadById(defaultLeadId && !selectedLead ? defaultLeadId : undefined);

  // Set default lead on load
  useEffect(() => {
    if (defaultLead && !selectedLead) {
      setSelectedLead(defaultLead);
      onChange(defaultLead.id, defaultLead);
    }
  }, [defaultLead]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external value changes
  useEffect(() => {
    if (!value && selectedLead) {
      setSelectedLead(null);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (lead: LeadSearchResult) => {
    setSelectedLead(lead);
    onChange(lead.id, lead);
    setOpen(false);
    setQuery("");
  };

  const handleClear = () => {
    setSelectedLead(null);
    onChange(null, null);
    setQuery("");
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .slice(0, 2)
      .map((w) => w.charAt(0))
      .join("")
      .toUpperCase();
  };

  // Color from name hash
  const getAvatarColor = (name: string) => {
    const colors = [
      "bg-primary/20 text-primary",
      "bg-secondary/20 text-secondary",
      "bg-accent/20 text-accent",
      "bg-warning/20 text-warning",
      "bg-success/20 text-success",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Selected lead card
  if (selectedLead) {
    return (
      <div className={cn("rounded-lg border bg-card p-3 flex items-center gap-3", className)}>
        <div
          className={cn(
            "h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0",
            getAvatarColor(selectedLead.name)
          )}
        >
          {getInitials(selectedLead.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{selectedLead.name}</p>
          <p className="text-2xs text-muted-foreground truncate">{selectedLead.email}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={handleClear}
          disabled={disabled}
          aria-label="Remover lead selecionado"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-10",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Search className="mr-2 h-4 w-4 flex-shrink-0" />
          Buscar lead por nome, email ou empresa...
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar lead..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? "Buscando..."
                : query.length >= 2
                  ? `Nenhum lead encontrado para "${query}"`
                  : "Digite para buscar leads..."}
            </CommandEmpty>
            <CommandGroup heading={query.length < 2 ? "Recentes" : "Resultados"}>
              {leads.map((lead) => (
                <CommandItem
                  key={lead.id}
                  value={lead.id}
                  onSelect={() => handleSelect(lead)}
                  className="flex items-center gap-3 py-2.5"
                >
                  <div
                    className={cn(
                      "h-7 w-7 rounded-full flex items-center justify-center text-2xs font-semibold flex-shrink-0",
                      getAvatarColor(lead.name)
                    )}
                  >
                    {getInitials(lead.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{lead.name}</p>
                    <p className="text-2xs text-muted-foreground truncate">{lead.email}</p>
                  </div>
                  {lead.companies?.name && (
                    <Badge variant="secondary" className="text-2xs flex-shrink-0">
                      {lead.companies.name}
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
